import { CircularProgress, Checkbox as MUICheckbox } from "@mui/material";
import {
    Document,
    useAddChatbotDocumentsMutation,
    useChatbotDocumentsQuery,
    useDocumentContextCapacityQuery,
} from "ApiLayer/Chatbot/queryChatbotDocuments";
import { Modal } from "antd";
import MUITheme from "components/MUITheme";
import { Button } from "components/atoms/Button";
import Tooltip from "components/atoms/Tooltip";
import { Paragraph } from "components/atoms/Typography";
import DocumentUploadModal from "components/organisms/Modals/DocumentUploadModal/DocumentUploadModal";
import useAlert from "hooks/useAlert";
import { useEffect, useState } from "react";
import { getFileTypeBadge } from "utils/helpers";
import styles from "./DocumentSelectionModal.module.scss";

interface DocumentSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddDocuments: (documentUids: string[]) => void;
    buildingModelUid: string;
}

function DocumentSelectionModal({ isOpen, onClose, onAddDocuments, buildingModelUid }: DocumentSelectionModalProps) {
    const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const { data: documents, isLoading } = useChatbotDocumentsQuery(buildingModelUid);
    const { data: capacityInfo, refetch: refetchCapacity } = useDocumentContextCapacityQuery(buildingModelUid);
    const { mutate: addDocuments, isLoading: isAdding } = useAddChatbotDocumentsMutation();
    const alert = useAlert();

    useEffect(() => {
        if (!isOpen) {
            setSelectedUids(new Set());
            setSearchQuery("");
        } else {
            refetchCapacity();
        }
    }, [isOpen, refetchCapacity]);

    const filteredDocuments =
        documents?.filter((doc) => doc.file_name.toLowerCase().includes(searchQuery.toLowerCase())) || [];

    const handleToggleSelection = (uploadUid: string) => {
        setSelectedUids((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(uploadUid)) {
                newSet.delete(uploadUid);
            } else {
                newSet.add(uploadUid);
            }
            return newSet;
        });
    };

    const handleAddDocuments = () => {
        const uids = Array.from(selectedUids);
        addDocuments(
            {
                buildingModelUid,
                documentUids: uids,
            },
            {
                onSuccess: (result) => {
                    const addedUids = result.added?.map((d: { upload_uid: string }) => d.upload_uid) || [];
                    const failed = result.failed || [];

                    if (addedUids.length > 0) {
                        onAddDocuments(addedUids);
                        if (failed.length === 0) {
                            alert.success(
                                `Successfully added ${addedUids.length} document${addedUids.length !== 1 ? "s" : ""}`
                            );
                        } else {
                            alert.success(
                                `Added ${addedUids.length} document${addedUids.length !== 1 ? "s" : ""}. ${failed.length} document${failed.length !== 1 ? "s" : ""} could not be added.`
                            );
                        }
                    }

                    if (failed.length > 0) {
                        const failedMessages = failed
                            .map((f: { error: string; upload_uid: string }) => {
                                const doc = documents?.find((d) => d.upload_uid === f.upload_uid);
                                const docName = doc?.file_name || f.upload_uid;
                                return `${docName}: ${f.error}`;
                            })
                            .join("; ");
                        alert.error(failedMessages);
                    }

                    if (failed.length === uids.length) {
                        return;
                    }
                    onClose();
                },
            }
        );
    };

    const isDocumentSelectable = (doc: Document) => doc.parsing_status === "completed" && doc.has_blocks !== false;

    const getParsingStatusInfo = (doc: Document) => {
        switch (doc.parsing_status) {
            case "pending":
            case "parsing":
                return {
                    icon: <CircularProgress size={14} style={{ color: "var(--audette-gray-500)" }} />,
                    tooltip: "Document is being processed...",
                    className: styles.statusProcessing,
                };
            case "failed":
                return {
                    icon: <span className={styles.statusIcon}>!</span>,
                    tooltip: doc.parsing_error || "Failed to process document",
                    className: styles.statusFailed,
                };
            case "too_big_for_parsing":
                return {
                    icon: <span className={styles.statusIcon}>!</span>,
                    tooltip: doc.parsing_error || "File is too large to parse",
                    className: styles.statusFailed,
                };
            case "non-parsable":
                return {
                    icon: <span className={styles.statusIcon}>!</span>,
                    tooltip: "This file type cannot be parsed",
                    className: styles.statusFailed,
                };
            case "skipped":
                return {
                    icon: <span className={styles.statusIcon}>–</span>,
                    tooltip: "Document was skipped",
                    className: styles.statusSkipped,
                };
            case "completed":
                if (doc.has_blocks === false) {
                    return {
                        icon: <span className={styles.statusIcon}>!</span>,
                        tooltip:
                            "This document could not be properly parsed. No structured content is available for citation.",
                        className: styles.statusFailed,
                    };
                }
                return null;
            default:
                return null;
        }
    };

    const getFileTypeIcon = (mimeType: string) => {
        const { label, variant } = getFileTypeBadge(mimeType);
        const variantClassMap: Record<string, string> = {
            pdf: "",
            html: styles.fileBadgeDefault,
            xls: styles.fileBadgeExcel,
            doc: styles.fileBadgeWord,
            ppt: styles.fileBadgeDefault,
            txt: styles.fileBadgeDefault,
            csv: styles.fileBadgeCsv,
            json: styles.fileBadgeJson,
            file: styles.fileBadgeDefault,
        };
        const variantClass = variantClassMap[variant] ?? styles.fileBadgeDefault;
        return <span className={`${styles.fileBadge} ${variantClass}`}>{label}</span>;
    };

    const getEmptyMessage = () => {
        if (searchQuery) {
            return "No documents match your search.";
        }
        return "No documents available.";
    };

    const getAddButtonText = () => {
        const documentText = selectedUids.size !== 1 ? "documents" : "document";
        return `Add ${selectedUids.size} ${documentText}`;
    };

    const formatTokens = (tokens: number): string => {
        if (tokens >= 1000000) {
            return `${(tokens / 1000000).toLocaleString("en-US", { maximumFractionDigits: 1 })}M`;
        }
        if (tokens >= 1000) {
            return `${(tokens / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}k`;
        }
        return tokens.toLocaleString();
    };

    const getCapacityText = () => {
        if (!capacityInfo) return null;
        return `${formatTokens(capacityInfo.current_tokens)}/${formatTokens(capacityInfo.max_tokens)} tokens used`;
    };

    return (
        <>
            <Modal
                open={isOpen}
                onCancel={onClose}
                title="Attach documents to chat"
                width={600}
                footer={
                    <div className={styles.footer}>
                        <Button
                            type="primary"
                            onClick={handleAddDocuments}
                            disabled={selectedUids.size === 0 || isAdding}
                        >
                            {isAdding ? (
                                <>
                                    <CircularProgress
                                        size={16}
                                        style={{
                                            color: "white",
                                            marginRight: "8px",
                                        }}
                                    />
                                    Adding...
                                </>
                            ) : (
                                getAddButtonText()
                            )}
                        </Button>
                        <Button
                            type="link"
                            onClick={onClose}
                            disabled={isAdding}
                            style={{ color: "var(--audette-black)" }}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            >
                <div className={styles.container}>
                    {capacityInfo && (
                        <div className={styles.capacityInfo}>
                            <span className={styles.capacityText}>{getCapacityText()}</span>
                        </div>
                    )}
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search in uploaded documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={isAdding}
                            className={styles.searchInput}
                        />
                        <Button
                            type="link"
                            onClick={() => setUploadModalOpen(true)}
                            disabled={isAdding}
                            className={styles.uploadButton}
                            style={{ fontSize: "0.9rem" }}
                        >
                            Upload new document(s)
                        </Button>
                    </div>

                    <div className={styles.parseDisclaimer}>
                        <Paragraph>Supported file types: PDF, DOCX, PPTX, XLSX (1 sheet) and HTML.</Paragraph>
                    </div>

                    {isLoading && (
                        <div className={styles.loading}>
                            <Paragraph>Loading documents...</Paragraph>
                        </div>
                    )}
                    {!isLoading && filteredDocuments.length === 0 && (
                        <div className={styles.empty}>
                            <Paragraph>{getEmptyMessage()}</Paragraph>
                        </div>
                    )}
                    {!isLoading && filteredDocuments.length > 0 && (
                        <div className={styles.documentList}>
                            {filteredDocuments.map((doc) => {
                                const selectable = isDocumentSelectable(doc);
                                const statusInfo = getParsingStatusInfo(doc);
                                const isItemDisabled = isAdding || !selectable;

                                return (
                                    <div
                                        key={doc.upload_uid}
                                        className={`${styles.documentItem} ${selectedUids.has(doc.upload_uid) ? styles.selected : ""} ${isItemDisabled ? styles.disabled : ""}`}
                                        onClick={() => !isItemDisabled && handleToggleSelection(doc.upload_uid)}
                                        onKeyDown={(e) => {
                                            if (!isItemDisabled && (e.key === "Enter" || e.key === " ")) {
                                                e.preventDefault();
                                                handleToggleSelection(doc.upload_uid);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={isItemDisabled ? -1 : 0}
                                    >
                                        <MUITheme primaryColor="#eb03ad">
                                            <MUICheckbox
                                                checked={selectedUids.has(doc.upload_uid)}
                                                onChange={() =>
                                                    !isItemDisabled && handleToggleSelection(doc.upload_uid)
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                                disabled={isItemDisabled}
                                                disableRipple={true}
                                                style={{
                                                    padding: 0,
                                                    margin: 0,
                                                }}
                                            />
                                        </MUITheme>
                                        <span className={styles.fileIcon}>{getFileTypeIcon(doc.mime_type)}</span>
                                        <div className={styles.documentInfo}>
                                            <div className={styles.fileName} title={doc.file_name}>
                                                {doc.file_name}
                                            </div>
                                            <div className={styles.fileType}>{doc.mime_type}</div>
                                        </div>
                                        {statusInfo && (
                                            <Tooltip tooltipBody={statusInfo.tooltip}>
                                                <div className={`${styles.statusIndicator} ${statusInfo.className}`}>
                                                    {statusInfo.icon}
                                                </div>
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Modal>
            <DocumentUploadModal modalIsOpen={uploadModalOpen} onCancel={() => setUploadModalOpen(false)} />
        </>
    );
}

export default DocumentSelectionModal;
